import { useQuery } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import getUserAvatar from '../common/utils/getUserAvatar';
import getSentenceInitials from '../common/utils/getSentenceInitials';

const searchUsersQuery = `
  query SearchUsers($orgID: ID!, $searchString: String!, $nextToken: String) {
    searchUsers(
      filter: {
        name: { wildcard: $searchString }
        orgID: { eq: $orgID }
        status: { ne: "deleted" }
      }
      limit: 750
      sort: { direction: asc, field: name }
      nextToken: $nextToken
    ) {
      items {
        id
        name
        email
        imageName
        imageType
        initials
        status
        role
        functionalRoles
        title
        avatarID
      }
      nextToken
    }
  }
`;

const fetchUsers = async (orgID: string, searchString: string) => {
  const users: any[] = [];
  let nextToken: string | null = null;

  do {
    const res: any = await API.graphql(
      graphqlOperation(searchUsersQuery, {
        orgID,
        searchString: searchString || '*',
        nextToken,
      })
    );
    users.push(...res.data.searchUsers.items);
    nextToken = res.data.searchUsers.nextToken;
  } while (nextToken);

  const processed = await Promise.all(
    users.map(async (user) => {
      const avatar = await getUserAvatar(user.imageName, user.imageType, true);
      const initials = user.initials || getSentenceInitials(user.name);
      return { ...user, avatar, initials };
    })
  );

  return processed;
};

export default function useListUsers(searchString = '*') {
  const orgID = (global as any).me?.orgID;

  const { data, isLoading, error } = useQuery({
    queryKey: ['listUsers', orgID, searchString],
    queryFn: () => fetchUsers(orgID, searchString),
    enabled: !!orgID,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { data: data ?? [], isLoading, error };
}
